import { Action, combine } from "@reactables/core";
import { from, defer, Observable } from "rxjs";
import { map, retry, withLatestFrom } from "rxjs/operators";
import { RxRequest, RxBuilder } from "@jauntin/reactables";
import PaymentService, {
  InitSessionPayload,
  InitSessionResponse,
} from "Helpers/PaymentService";
import PortalOneModalService from "Helpers/PortalOneModalService";
import UserEntryDataService from "Helpers/UserEntryDataService";

export const RxPayment = ({
  paymentService,
  userEntryDataService,
  portalOneModalService,
}: {
  paymentService: PaymentService;
  portalOneModalService: PortalOneModalService;
  userEntryDataService: UserEntryDataService;
}) => {
  const rxInitSessionRequest = RxRequest<
    InitSessionPayload,
    InitSessionResponse
  >({
    name: "paymentModalSession",
    resource: (payload) =>
      paymentService.initSession(payload).then(({ data }) => ({
        ...data,
        userEntryData: payload.userEntryData,
        dashedInsuredName: payload.dashedInsuredName,
      })),
  });

  const rxModalRenderError = RxBuilder({
    sources: [
      portalOneModalService.modalLoadError$.pipe(
        map(() => ({ type: "modalError" })),
      ),
    ],
    initialState: false,
    reducers: {
      modalError: () => true,
      resetError: () => false,
    },
  });

  const paymentComplete$ = portalOneModalService.paymentComplete$.pipe(
    withLatestFrom(rxInitSessionRequest[0]),
  );

  const rxConfirmPaymentRequest = RxRequest({
    sources: [
      paymentComplete$.pipe(
        map(([paymentComplete, initRequest]) => ({
          type: "send",
          payload: {
            event: paymentComplete,
            policyNumber: (initRequest.data as InitSessionResponse)
              .policyNumber,
            dashedInsuredName: (initRequest.data as InitSessionResponse)
              .dashedInsuredName,
          },
        })),
      ),
    ],
    effect: (
      obs$: Observable<
        Action<{
          event: CustomEvent;
          policyNumber: string;
          dashedInsuredName: string;
        }>
      >,
    ) => {
      const result$ = obs$.pipe(
        map(({ payload: { event, policyNumber, dashedInsuredName } }) => {
          return defer(() =>
            from(
              paymentService
                .confirmPayment({
                  policyNumber,
                  paymentCompleteEvent: event.detail,
                })
                .then(({ data }) => ({ ...data, dashedInsuredName })),
            ),
          ).pipe(
            retry({
              count: 3,
              delay: 1000,
            }),
          );
        }),
      );
      return result$;
    },
  });

  const rxSaveUserEntryData = RxRequest({
    sources: [
      paymentComplete$.pipe(
        map(([, initRequest]) => {
          const resp = initRequest.data as InitSessionResponse;
          return {
            type: "send",
            payload: {
              data: { ...resp.userEntryData.data, policyPurchased: true },
              formUniqId: resp.userEntryData.formUniqId,
            },
          };
        }),
      ),
    ],
    resource: (payload) => userEntryDataService.saveEntryData(payload),
  });

  return combine({
    initSessionRequest: rxInitSessionRequest,
    modalRenderError: rxModalRenderError,
    confirmPaymentRequest: rxConfirmPaymentRequest,
    saveUserEntryData: rxSaveUserEntryData,
  });
};
